import type { DetectionSlice, DetectionActions } from '@/types/store';
import type { SliceSet, SliceGet } from './types';
import type { CrossContactRisk } from '@allergen-lens/shared-logic';
import type { RegulatoryMode } from '@allergen-lens/shared-logic';
import { expandCompounds } from '@allergen-lens/shared-logic';
import { detectAllergens } from '@allergen-lens/shared-logic';
import { mergeDetections } from '@allergen-lens/shared-logic';
import { resolveSpecies } from '@allergen-lens/shared-logic';
import { generateDeclaration } from '@allergen-lens/shared-logic';
import { detectPAL } from '@allergen-lens/shared-logic';
import { detectCertifications } from '@allergen-lens/shared-logic';
import { parseIngredients } from '@allergen-lens/shared-logic';
import allergensData from '@/data/allergens.json';
import compoundsData from '@/data/compounds.json';
import type { AllergenEntry } from '@allergen-lens/shared-logic';
import type { CompoundIngredient } from '@allergen-lens/shared-logic';

const allergenLibrary = allergensData as AllergenEntry[];
const compoundLibrary = compoundsData as CompoundIngredient[];

type Slice = DetectionSlice & DetectionActions;

export const createDetectionSlice = (
  set: SliceSet,
  get: SliceGet
): Slice => ({
  results: [],
  declaration: null,
  isProcessing: false,
  palDetections: [],
  crossContactRisks: [],
  certificationMatches: [],

  runDetection: () => {
    const state = get();
    set(() => ({ isProcessing: true }));

    const expanded = expandCompounds(state.tokens, compoundLibrary);

    // Build merged library from base allergen library + active profile's custom allergens
    const activeProfile = state.profiles.find((p) => p.id === state.activeProfileId);
    let library = allergenLibrary;
    if (activeProfile?.customAllergens?.length) {
      const customEntries: AllergenEntry[] = activeProfile.customAllergens.map((ca) => ({
        id: ca.id,
        canonicalName: ca.name,
        category: 'custom',
        synonyms: ca.synonyms,
        isEU: true,
        isUS: true,
      }));
      library = [...allergenLibrary, ...customEntries];
    }

    let detections = detectAllergens(expanded, library, state.mode);
    detections = mergeDetections(detections);
    detections = resolveSpecies(detections, allergenLibrary);
    const declaration = generateDeclaration(expanded, detections, state.mode);
    const palDetections = detectPAL(state.rawInput);
    const certificationMatches = detectCertifications(state.rawInput);

    set(() => ({
      results: detections,
      declaration,
      isProcessing: false,
      palDetections,
      certificationMatches,
    }));
  },

  clearDetection: () =>
    set(() => ({
      results: [],
      declaration: null,
      isProcessing: false,
      palDetections: [],
      certificationMatches: [],
    })),

  addCrossContactRisk: (risk: CrossContactRisk) =>
    set((state) => ({
      crossContactRisks: [...state.crossContactRisks, risk],
    })),

  removeCrossContactRisk: (id: string) =>
    set((state) => ({
      crossContactRisks: state.crossContactRisks.filter((r) => r.id !== id),
    })),

  clearDemoData: () =>
    set(() => ({
      rawInput: '',
      tokens: [],
      parseError: null,
      results: [],
      declaration: null,
      isProcessing: false,
      palDetections: [],
      crossContactRisks: [],
      certificationMatches: [],
    })),

  loadDemoDataset: (rawInput: string, mode: RegulatoryMode) => {
    const parseResult = parseIngredients(rawInput);
    set(() => ({
      rawInput,
      tokens: parseResult.tokens,
      parseError: parseResult.error,
      mode,
      results: [],
      declaration: null,
      isProcessing: false,
      palDetections: [],
      crossContactRisks: [],
      certificationMatches: [],
    }));
  },
});
